import {
  BsBookmarkCheckFill,
  BsBookmarkPlusFill,
  BsBoxArrowUpRight,
  BsClockHistory,
  BsFillPlayFill,
  BsFillStarFill,
} from "react-icons/bs";
import { Link } from "react-router-dom";
import styled from "styled-components";
import Button from "../Button";
import useFavorites from "../../hooks/useFavorites";
import {
  formatFullDate,
  formatRuntime,
  formatVoteAverage,
  getBackdropUrl,
  getPosterUrl,
} from "../../utils/movie";

const Wrapper = styled.div`
  display: grid;
  gap: 2rem;
`;

const Header = styled.div`
  display: grid;
  gap: 1.5rem;

  @media (min-width: ${({ theme }) => theme.breakpoints.desktop}) {
    grid-template-columns: minmax(240px, 300px) minmax(0, 1fr);
    align-items: start;
  }
`;

const PosterCard = styled.div`
  overflow: hidden;
  border-radius: ${({ theme }) => theme.radii.lg};
  background: ${({ theme }) => theme.colors.backgroundMuted};
  box-shadow: ${({ theme }) => theme.shadows.soft};
  aspect-ratio: 2 / 3;
`;

const Poster = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const Summary = styled.div`
  display: grid;
  gap: 1rem;
`;

const Title = styled.h2`
  margin: 0;
  font-size: clamp(2rem, 4vw, 3rem);
`;

const Tagline = styled.p`
  margin: 0;
  color: ${({ theme }) => theme.colors.textMuted};
  font-size: 1rem;
`;

const Meta = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.9rem;
  color: ${({ theme }) => theme.colors.textMuted};
`;

const MetaItem = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.45rem;
`;

const Overview = styled.p`
  margin: 0;
  color: ${({ theme }) => theme.colors.textMuted};
  line-height: 1.8;
`;

const GenreList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.65rem;
`;

const Genre = styled.span`
  padding: 0.45rem 0.9rem;
  border-radius: ${({ theme }) => theme.radii.pill};
  background: rgba(255, 255, 255, 0.08);
  border: 1px solid ${({ theme }) => theme.colors.border};
  font-size: 0.85rem;
`;

const ActionRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.85rem;
`;

const VideoFrame = styled.div`
  overflow: hidden;
  border-radius: ${({ theme }) => theme.radii.lg};
  border: 1px solid ${({ theme }) => theme.colors.border};
  background: ${({ theme }) => theme.colors.backgroundElevated};
  aspect-ratio: 16 / 9;

  iframe {
    width: 100%;
    height: 100%;
    border: 0;
  }
`;

const DetailGrid = styled.div`
  display: grid;
  gap: 1.25rem;

  @media (min-width: ${({ theme }) => theme.breakpoints.desktop}) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
`;

const Block = styled.section`
  display: grid;
  gap: 0.9rem;
  padding: 1.3rem;
  border-radius: ${({ theme }) => theme.radii.md};
  border: 1px solid ${({ theme }) => theme.colors.border};
  background: rgba(255, 255, 255, 0.04);
`;

const BlockTitle = styled.h3`
  margin: 0;
  font-size: 1.1rem;
`;

const CastGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
  gap: 0.85rem;
`;

const CastCard = styled.article`
  display: grid;
  gap: 0.75rem;
  padding: 0.95rem;
  border-radius: ${({ theme }) => theme.radii.md};
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid rgba(255, 255, 255, 0.06);
`;

const CastImage = styled.div`
  width: 56px;
  height: 56px;
  border-radius: 50%;
  overflow: hidden;
  background: linear-gradient(135deg, rgba(229, 9, 20, 0.85), rgba(255, 255, 255, 0.14));

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const CastInfo = styled.div`
  display: grid;
  gap: 0.2rem;
`;

const CastName = styled.strong`
  font-size: 0.94rem;
`;

const CastRole = styled.span`
  display: block;
  color: ${({ theme }) => theme.colors.textMuted};
  font-size: 0.85rem;
`;

const RecommendationGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 0.95rem;
`;

const RecommendationCard = styled.button`
  display: grid;
  gap: 0.7rem;
  padding: 0;
  text-align: left;
  cursor: pointer;
`;

const RecommendationThumb = styled.div`
  overflow: hidden;
  aspect-ratio: 16 / 9;
  border-radius: ${({ theme }) => theme.radii.md};
  background: ${({ theme }) => theme.colors.backgroundMuted};

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const RecommendationTitle = styled.strong`
  display: block;
  font-size: 0.95rem;
`;

function MovieDetailsContent({ movie, mode = "page", onSelectMovie }) {
  const { toggleFavorite, isFavorite } = useFavorites();
  const favorite = isFavorite(movie.id);
  const posterUrl = getPosterUrl(movie.posterPath) || getBackdropUrl(movie.backdropPath, "w780");
  const trailerEmbedUrl = movie.trailer?.key
    ? `https://www.youtube.com/embed/${movie.trailer.key}?autoplay=0&rel=0`
    : "";

  return (
    <Wrapper>
      <Header>
        <PosterCard>
          {posterUrl ? <Poster src={posterUrl} alt={movie.title} loading="lazy" /> : null}
        </PosterCard>
        <Summary>
          <Title>{movie.title}</Title>
          {movie.tagline ? <Tagline>{movie.tagline}</Tagline> : null}
          <Meta>
            <MetaItem>
              <BsFillStarFill color="#f5c518" aria-hidden />
              {formatVoteAverage(movie.rating)} / 10
            </MetaItem>
            <MetaItem>
              <BsClockHistory aria-hidden />
              {formatRuntime(movie.runtime)}
            </MetaItem>
            <MetaItem>{formatFullDate(movie.releaseDate)}</MetaItem>
            {movie.status ? <MetaItem>{movie.status}</MetaItem> : null}
          </Meta>
          <Overview>{movie.overview || "Sinopse indisponivel no momento."}</Overview>
          <GenreList>
            {(movie.genreNames || []).map((genre) => (
              <Genre key={genre}>{genre}</Genre>
            ))}
          </GenreList>
          <ActionRow>
            {movie.trailer?.url ? (
              <Button as="a" href={movie.trailer.url} target="_blank" rel="noreferrer" icon={<BsFillPlayFill />}>
                Ver trailer
              </Button>
            ) : null}
            <Button
              variant={favorite ? "secondary" : "primary"}
              icon={favorite ? <BsBookmarkCheckFill /> : <BsBookmarkPlusFill />}
              onClick={() => toggleFavorite(movie)}
            >
              {favorite ? "Na sua lista" : "Salvar na lista"}
            </Button>
            {mode === "modal" ? (
              <Button as={Link} to={`/filme/${movie.id}`} variant="ghost" icon={<BsBoxArrowUpRight />}>
                Abrir pagina completa
              </Button>
            ) : null}
          </ActionRow>
        </Summary>
      </Header>

      {trailerEmbedUrl ? (
        <section>
          <BlockTitle>Trailer</BlockTitle>
          <VideoFrame>
            <iframe
              src={trailerEmbedUrl}
              title={`Trailer de ${movie.title}`}
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            />
          </VideoFrame>
        </section>
      ) : null}

      <DetailGrid>
        {movie.cast?.length ? (
          <Block>
            <BlockTitle>Elenco principal</BlockTitle>
            <CastGrid>
              {movie.cast.map((castMember) => {
                const imageUrl = getPosterUrl(castMember.profile_path, "w185");

                return (
                  <CastCard key={castMember.id}>
                    <CastImage>
                      {imageUrl ? <img src={imageUrl} alt={castMember.name} loading="lazy" /> : null}
                    </CastImage>
                    <CastInfo>
                      <CastName>{castMember.name}</CastName>
                      <CastRole>{castMember.character || "Personagem nao informado"}</CastRole>
                    </CastInfo>
                  </CastCard>
                );
              })}
            </CastGrid>
          </Block>
        ) : null}

        {movie.recommendations?.length ? (
          <Block>
            <BlockTitle>Recomendacoes similares</BlockTitle>
            <RecommendationGrid>
              {movie.recommendations.slice(0, 6).map((item) => (
                <RecommendationCard key={item.id} type="button" onClick={() => onSelectMovie(item.id)}>
                  <RecommendationThumb>
                    {item.backdropPath ? (
                      <img src={getBackdropUrl(item.backdropPath, "w780")} alt={item.title} loading="lazy" />
                    ) : null}
                  </RecommendationThumb>
                  <RecommendationTitle>{item.title}</RecommendationTitle>
                </RecommendationCard>
              ))}
            </RecommendationGrid>
          </Block>
        ) : null}
      </DetailGrid>
    </Wrapper>
  );
}

export default MovieDetailsContent;
